import { useEffect, useRef, useState } from "react";
import {
    Box,
    Card,
    Stack,
    Typography,
    Button,
    Divider,
    Modal,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import Scrollbar from "src/components/scrollbar/Scrollbar";
import { HotTable } from '@handsontable/react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorModal from "src/components/FastReportErrorModal/ErrorModal";
import Handsontable from 'handsontable';
import Services from "src/services/Services";
import { WarningOutlined } from '@mui/icons-material';
import { registerAllModules } from "handsontable/registry";

registerAllModules();

export default function ArasReport({ parsedUser, language, tabs }) {
    const calculatedHeight = window.innerHeight - 250;
    const initialRows = Array.from({ length: 50 }, (_, index) => ({
        id: index + 1,
        inBranchDate: '',
        barcode: '',
        customerName: '',
        staff: '',
        description: '',
        processingType: '',
        price: '',
        paymentType: '',
        processingDetails: '',
    }));

    const [tableData, setTableData] = useState(initialRows);
    const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [failedRowsCount, setFailedRowsCount] = useState(0);
    const [modalType, setModalType] = useState('success');
    const hotRef = useRef(null);

    // ---------- DATE HELPERS ----------
    const toTodayStr = () => {
        const t = new Date();
        const dd = String(t.getDate()).padStart(2, '0');
        const mm = String(t.getMonth() + 1).padStart(2, '0');
        const yyyy = t.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    function parseDDMMYYYY(value) {
        if (!value || typeof value !== 'string') return null;
        const norm = value.replace(/\./g, '/').replace(/-/g, '/');
        const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(norm);
        if (!m) return null;
        const dd = parseInt(m[1], 10);
        const mm = parseInt(m[2], 10);
        const yyyy = parseInt(m[3], 10);
        const d = new Date(yyyy, mm - 1, dd);
        if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
        return d;
    }

    function isFuture(dateObj) {
        if (!(dateObj instanceof Date)) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const d = new Date(dateObj);
        d.setHours(0, 0, 0, 0);
        return d.getTime() > today.getTime();
    }

    function convertDateFormat(dateStr) {
        if (!dateStr || typeof dateStr !== 'string') {
            return '';
        }
        const parts = dateStr.split('/');
        if (parts.length !== 3) {
            return dateStr;
        }
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const getColumnHeaders = () => [
        'NO',
        'KARGO GELİŞ TARİHİ',
        'GÖNDERİ NO',
        'MÜŞTERİ AD SOYAD',
        'EP PERSONAL AD SOYAD',
        'AÇIKLAMA',
        'İŞLEM TÜRÜ',
        'TUTAR',
        'ÖDEME TÜRÜ',
        'İŞLEM DETAY',
        'LOKASYON',
    ];

    const getColumnSettings = () => {
        const headers = getColumnHeaders();
        const todayDate = new Date();

        return headers.map((header) => {
            if (header === 'NO') {
                return {
                    data: null,
                    readOnly: true,
                    className: 'htCenter htMiddle',
                    renderer: (instance, td, row) => {
                        Handsontable.dom.empty(td);
                        Handsontable.dom.fastInnerHTML(td, row + 1);
                        return td;
                    },
                };
            }
            if (header === 'LOKASYON') {
                return {
                    type: 'text',
                    strict: false,
                    className: 'htCenter htMiddle',
                    placeholder: parsedUser.firstResponsibleOffice.title,
                    readOnly: true,
                };
            }
            if (header === 'ÖDEME TÜRÜ') {
                return {
                    type: 'dropdown',
                    strict: true,
                    allowInvalid: false,
                    className: 'htCenter htMiddle',
                    source: ["KREDİ KARTI", "NAKİT", "KARŞI ÖDEMELİ", "İADE"]
                };
            }
            if (header === 'İŞLEM DETAY') {
                return {
                    type: 'dropdown',
                    strict: true,
                    allowInvalid: false,
                    className: 'htCenter htMiddle',
                    source: ["ANLAŞMALI", "ÜCRETLİ GÖNDERİ", "İADE"]
                };
            }
            if (header === 'KARGO GELİŞ TARİHİ') {
                // Gelecek tarih seçilemesin + yazılamasın + yazılırsa bugüne dönsün
                return {
                    type: 'date',
                    dateFormat: 'DD/MM/YYYY',
                    correctFormat: true,
                    allowInvalid: false,
                    strict: true,
                    className: 'htCenter htMiddle',
                    placeholder: 'GG/AA/YYYY',
                    datePickerConfig: {
                        firstDay: 1,
                        showWeekNumber: true,
                        maxDate: todayDate, // geleceğe seçim yok
                        i18n: {
                            previousMonth: 'Previous Month',
                            nextMonth: 'Next Month',
                            months: [
                                'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                                'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
                            ],
                            weekdays: ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'],
                            weekdaysShort: ['Pa', 'Pzt', 'Salı', 'Ça', 'Pe', 'Cu', 'Ct'],
                        },
                    },
                    validator: (value, callback) => {
                        const d = parseDDMMYYYY(value);
                        if (!d) return callback(false);
                        if (isFuture(d)) return callback(false);
                        return callback(true);
                    },
                };
            }
            if (header === 'TUTAR') {
                return { type: 'numeric', strict: false, className: 'htCenter htMiddle' };
            }
            if (header === 'İŞLEM TÜRÜ') {
                return {
                    type: 'dropdown',
                    strict: true,
                    allowInvalid: false,
                    className: 'htCenter htMiddle',
                    source: ['İade', 'Satış'],
                };
            }
            if (['GÖNDERİ NO', 'MÜŞTERİ AD SOYAD', 'EP PERSONAL AD SOYAD', 'AÇIKLAMA', 'ÖDEME TÜRÜ', 'İŞLEM DETAY'].includes(header)) {
                return { type: 'text', strict: false, className: 'htCenter htMiddle' };
            }
            return { type: 'text', strict: false, className: 'htCenter htMiddle' };
        });
    };

    // Otomatik büyük harf + tarih normalize + GELECEK TARİHİ BUGÜNE ÇEK
    const onAfterChange = (changes, source) => {
        if (!hotRef.current || source === 'setDataAtCell') return;
        const hotInstance = hotRef.current.hotInstance;
        const headers = getColumnHeaders();
        const dataToSet = [];

        changes?.forEach(([row, col, prevValue, newValue]) => {
            const header = headers[col];

            // Metin alanlarını TR upper
            if ([2, 3, 4, 5, 8, 9].includes(col) && typeof newValue === 'string') {
                const upperValue = newValue.toLocaleUpperCase('tr-TR');
                if (upperValue !== newValue) {
                    dataToSet.push([row, col, upperValue]);
                }
            }

            // Tarih normalize
            if (header?.includes('TARİH') && newValue) {
                let formatted = String(newValue).replace(/\./g, '/').replace(/-/g, '/');
                if (!formatted.includes('/') && formatted.length === 8) {
                    formatted = formatted.replace(/(\d{2})(\d{2})(\d{4})/, '$1/$2/$3');
                }

                // KARGO GELİŞ TARİHİ gelecekte ise bugüne çevir
                if (header === 'KARGO GELİŞ TARİHİ') {
                    const d = parseDDMMYYYY(formatted);
                    if (d && isFuture(d)) {
                        formatted = toTodayStr();
                    }
                }

                dataToSet.push([row, col, formatted]);
            }
        });

        if (dataToSet.length > 0) {
            hotInstance.setDataAtCell(dataToSet, 'setDataAtCell');
        }
    };

    useEffect(() => {
        if (hotRef.current) {
            const hotInstance = hotRef.current.hotInstance;
            hotInstance.loadData(tableData);
            hotInstance.validateCells();
        }
    }, [tableData]);

    useEffect(() => {
        if (isSuccessModalOpen && hotRef.current) {
            setTimeout(() => {
                hotRef.current.hotInstance.validateCells(() => {
                    hotRef.current.hotInstance.render();
                });
            }, 0);
        }
    }, [isSuccessModalOpen]);

    // KARGO GELİŞ TARİHİ kolonunda klavye ile yazmayı engelle
    useEffect(() => {
        if (!hotRef.current) return;
        const hot = hotRef.current.hotInstance;

        const headers = getColumnHeaders();
        const arrivalColIndex = headers.indexOf('KARGO GELİŞ TARİHİ');

        const blockTyping = (e) => {
            const sel = hot.getSelectedLast();
            if (!sel) return;
            const col = sel[1];
            if (col !== arrivalColIndex) return;

            const isCharacterOrEdit =
                (e.key && e.key.length === 1) ||
                e.key === 'Backspace' ||
                e.key === 'Delete' ||
                e.key === ' ' ||
                e.key === 'Enter';

            if (isCharacterOrEdit) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        };

        hot.addHook('beforeKeyDown', blockTyping);
        return () => {
            try { hot.removeHook('beforeKeyDown', blockTyping); } catch {}
        };
    }, []);

    const handleCreate = () => {
        const hotInstance = hotRef.current.hotInstance;
        const headers = getColumnHeaders();

        hotInstance.validateCells(() => {
            const currentData = hotInstance.getData();
            const validRows = [];
            const errorRows = [];

            currentData.forEach((row, rowIndex) => {
                let hasRequiredData = false;
                let hasError = false;

                for (let col = 0; col < headers.length; col++) {
                    const header = headers[col];
                    const cellValue = row[col];
                    const meta = hotInstance.getCellMeta(rowIndex, col);

                    // AÇIKLAMA ve TUTAR dışındaki zorunlu kolonları kontrol et
                    const isRequiredColumn = !['NO', 'LOKASYON', 'AÇIKLAMA', 'TUTAR'].includes(header);

                    if (header === 'KARGO GELİŞ TARİHİ') {
                        // Son güvenlik: gelecekte ise bugüne setle
                        const d = parseDDMMYYYY(cellValue);
                        if (d && isFuture(d)) {
                            row[col] = toTodayStr();
                        }
                    }

                    if (isRequiredColumn) {
                        if (!cellValue || cellValue.toString().trim() === '' || meta.valid === false) {
                            hasError = true;
                        } else {
                            hasRequiredData = true;
                        }
                    }
                }

                if (hasRequiredData) {
                    if (hasError) errorRows.push(row);
                    else validRows.push(row);
                }
            });

            if (validRows.length === 0) {
                openSuccessModal('İşlenecek geçerli satır yok.', 0, 'error');
                return;
            }

            const reportArray = validRows.map((row) => ({
                inBranchDate: convertDateFormat(row[1]),
                barcode: row[2],
                customerName: row[3],
                staff: row[4],
                description: row[5] || "",
                processingType: row[6],
                price: row[7]?.toString() || "",
                paymentType: row[8],
                processingDetails: row[9],
            }));

            Services.CreateOperationReport(parsedUser.token, reportArray, 'aras-kargo')
                .then((response) => {
                    if (response.data.status) {
                        const message = errorRows.length === 0
                            ? 'Geçerli satırlar başarıyla işlendi'
                            : 'Geçerli satırlar işlendi. Hatalı satır sayısı';
                        openSuccessModal(message, errorRows.length, errorRows.length ? 'error' : 'success');
                    } else {
                        openSuccessModal('Bir hata meydana geldi.', 0, 'error');
                    }
                })
                .catch((e) => {
                    console.error(e);
                    openSuccessModal('Servis çağrısı sırasında hata oluştu.', reportArray.length, 'error');
                });

            // Hatalı satırları tekrar tabloya yerleştir
            const totalRows = 50;
            let newData = [...errorRows];
            while (newData.length < totalRows) {
                let emptyRow = Array(headers.length).fill('');
                emptyRow[0] = newData.length + 1;
                newData.push(emptyRow);
            }
            setTableData(newData);
        });
    };

    const openSuccessModal = (message, count, type) => {
        setSuccessMessage(message);
        setFailedRowsCount(count);
        setModalType(type);
        setSuccessModalOpen(true);
    };

    useEffect(() => {
        if (hotRef.current) {
            const hotInstance = hotRef.current.hotInstance;
            hotInstance.addHook('afterChange', onAfterChange);
            hotInstance.updateSettings({
                data: tableData,
                colHeaders: getColumnHeaders(),
                columns: getColumnSettings(),
            });
        }
    }, [tableData]);

    return (
        <div>
            <Stack direction="row" justifyContent='space-between' mb={2}>
                {tabs}
                <Button onClick={handleCreate} variant="contained" endIcon={<SendIcon />}>
                    {language.commons.save}
                </Button>
            </Stack>
            <Card>
                <Scrollbar>
                    <Box sx={{ width: '100%' }}>
                        <HotTable
                            ref={hotRef}
                            rowHeights={35}
                            licenseKey="non-commercial-and-evaluation"
                            data={tableData}
                            persistentState={true}
                            colHeaders={getColumnHeaders()}
                            columns={getColumnSettings()}
                            stretchH="all"
                            height={calculatedHeight}
                        />
                    </Box>
                </Scrollbar>
            </Card>
            <Modal open={isSuccessModalOpen} onClose={() => setSuccessModalOpen(false)}>
                <Box
                    sx={{
                        position: 'absolute',
                        width: 400,
                        bgcolor: 'background.paper',
                        border: '2px solid #000',
                        boxShadow: 24,
                        p: 4,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                    }}
                >
                    {modalType === 'error' ? (
                        <WarningOutlined sx={{ color: 'orange', fontSize: 64 }} />
                    ) : (
                        <CheckCircleIcon sx={{ color: 'green', fontSize: 64 }} />
                    )}
                    <Typography variant="h6">{successMessage}</Typography>
                    {failedRowsCount > 0 && (
                        <>
                            <Divider sx={{ my: 2, backgroundColor: 'gray' }} />
                            <Typography variant="h6">
                                Uyarı: {failedRowsCount} satırda hata var.
                            </Typography>
                        </>
                    )}
                    <Button
                        onClick={() => {
                            setSuccessModalOpen(false);
                            if (hotRef.current) {
                                setTimeout(() => {
                                    hotRef.current.hotInstance.validateCells(() => {
                                        hotRef.current.hotInstance.render();
                                    });
                                }, 50);
                            }
                        }}
                    >
                        {language.commons.okay}
                    </Button>
                </Box>
            </Modal>
            <ErrorModal />
        </div>
    );
}